
import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';

const ContactPage = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const contactMethods = [
    {
      icon: 'ri-mail-line',
      title: 'Email Me',
      value: 'titilayoajayi923@gmail.com',
      link: 'mailto:titilayoajayi923@gmail.com',
      description: 'Best for detailed inquiries',
      color: 'primary-sage',
    },
    {
      icon: 'ri-phone-line',
      title: 'Call Me',
      value: ['+234 906 081 4354', '+234 904 739 0448'],
      link: 'tel:+2349060814354',
      description: 'Available Mon-Fri, 9AM-5PM WAT',
      color: 'primary-gold',
    },
    {
      icon: 'ri-whatsapp-line',
      title: 'Chat on WhatsApp',
      value: '+234 904 739 0448',
      link: 'https://wa.me/2349047390448',
      description: 'Fastest response time',
      color: 'primary-sage',
    },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.service) {
      setSubmitMessage('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const submitData = new URLSearchParams();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('service', formData.service);
      submitData.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5p699lo3ikh5o9dul70', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you! I\'ll get back to you within 24 hours.');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitMessage('Something went wrong. Please try again or contact me directly.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again or contact me directly.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 8000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero */}
        <section ref={heroRef} className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-serif font-bold text-5xl lg:text-6xl text-primary-charcoal mb-6">
                Let's Start Your Transformation
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Choose your preferred way to connect
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="pb-24 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {contactMethods.map((method, index) => (
                <motion.a
                  key={index}
                  href={method.link}
                  target={method.link.startsWith('http') ? '_blank' : undefined}
                  rel={method.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                  className="bg-primary-beige rounded-3xl p-8 text-center hover:shadow-2xl transition-all duration-300 cursor-pointer"
                >
                  <div className={`w-16 h-16 flex items-center justify-center bg-${method.color} rounded-full mx-auto mb-6`}>
                    <i className={`${method.icon} text-3xl text-white`}></i>
                  </div>

                  <h3 className="font-serif font-bold text-2xl text-primary-charcoal mb-3">
                    {method.title}
                  </h3>

                  {Array.isArray(method.value) ? (
                    <div className="space-y-1 mb-3">
                      {method.value.map((val, idx) => (
                        <p key={idx} className="text-primary-gold font-medium">{val}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-primary-gold font-medium mb-3 break-all px-4">{method.value}</p>
                  )}

                  <p className="text-sm text-gray-600">{method.description}</p>
                </motion.a>
              ))}
            </div>
          </div>
        </section>

        {/* Booking Form */}
        <section className="py-24 bg-primary-beige">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="font-serif font-bold text-4xl text-primary-charcoal mb-4">
                Schedule a Free Consultation
              </h2>
              <p className="text-lg text-gray-600">
                Fill out the form below and I'll get back to you within 24 hours
              </p>
            </motion.div>

            <motion.form
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              onSubmit={handleSubmit}
              id="contact-form"
              data-readdy-form
              className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl"
            >
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-primary-charcoal mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-gold transition-all"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-primary-charcoal mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-gold transition-all"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-primary-charcoal mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-gold transition-all"
                    placeholder="+234 xxx xxx xxxx"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-primary-charcoal mb-2">
                    Service Interest <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-gold transition-all cursor-pointer"
                  >
                    <option value="">Select a service</option>
                    <option value="Business Consulting">Business Consulting</option>
                    <option value="E-commerce Management">E-commerce Management</option>
                    <option value="Health & Wellbeing Coaching">Health & Wellbeing Coaching</option>
                    <option value="Multiple Services">Multiple Services</option>
                    <option value="Not Sure">Not Sure - Need Guidance</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-semibold text-primary-charcoal mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-gold transition-all resize-none"
                  placeholder="Tell me about your goals and how I can help..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
              </div>

              {submitMessage && (
                <div className={`mb-6 p-4 rounded-lg ${submitMessage.includes('Thank you') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {submitMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-primary-gold hover:bg-accent-warm-gold text-white font-bold text-lg rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting...' : 'Book My Session'}
                {!isSubmitting && <i className="ri-calendar-check-line ml-2"></i>}
              </button>
            </motion.form>
          </div>
        </section>

        {/* Social Links */}
        <section className="py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="font-serif font-bold text-3xl text-primary-charcoal mb-8">
              Connect on Social Media
            </h2>

            <div className="flex flex-wrap items-center justify-center gap-6">
              <a
                href="https://x.com/Oluwaseyilog"
                target="_blank"
                rel="noopener noreferrer"
                className="w-16 h-16 flex items-center justify-center bg-primary-beige hover:bg-primary-sage text-primary-charcoal hover:text-white rounded-full transition-all duration-300 cursor-pointer"
                aria-label="X (Twitter)"
              >
                <i className="ri-twitter-x-line text-2xl"></i>
              </a>
              <a
                href="https://www.youtube.com/@TitilayoAjayi-q2w"
                target="_blank"
                rel="noopener noreferrer"
                className="w-16 h-16 flex items-center justify-center bg-primary-beige hover:bg-primary-sage text-primary-charcoal hover:text-white rounded-full transition-all duration-300 cursor-pointer"
                aria-label="YouTube"
              >
                <i className="ri-youtube-line text-2xl"></i>
              </a>
              <a
                href="https://www.instagram.com/aanuo_luwa"
                target="_blank"
                rel="noopener noreferrer"
                className="w-16 h-16 flex items-center justify-center bg-primary-beige hover:bg-primary-sage text-primary-charcoal hover:text-white rounded-full transition-all duration-300 cursor-pointer"
                aria-label="Instagram"
              >
                <i className="ri-instagram-line text-2xl"></i>
              </a>
              <a
                href="https://www.facebook.com/TitilayoAjayi"
                target="_blank"
                rel="noopener noreferrer"
                className="w-16 h-16 flex items-center justify-center bg-primary-beige hover:bg-primary-sage text-primary-charcoal hover:text-white rounded-full transition-all duration-300 cursor-pointer"
                aria-label="Facebook"
              >
                <i className="ri-facebook-fill text-2xl"></i>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ContactPage;
